import { useEffect, useRef, useState } from 'react'
import api from '@/lib/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { PageHeader } from '@/components/dashboard-ui'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import { splitBrandName } from '@/hooks/useBrand'
import { Upload, Globe, Palette, Loader2, Check, MessageSquare } from 'lucide-react'

interface PlatformBranding {
  appName?: string
  logoUrl?: string
  faviconUrl?: string
  primaryColor?: string
  supportEmail?: string
  poweredByEnabled?: boolean
  poweredByText?: string
  poweredByLogoUrl?: string
  poweredByUrl?: string
}

interface BrandingResponse {
  branding: PlatformBranding
  customDomain: string | null
  subdomainSlug: string
  platformDomain: string | null
  connectHost: string | null
}

interface DomainStatus {
  domain: string | null
  connected: boolean
  cname?: { ok: boolean; found: string | null }
  cloudflare?: { status: string; sslStatus?: string }
}

const HEX_RE = /^#[0-9a-fA-F]{6}$/

export default function FranchiseBrandingPage() {
  const { toast } = useToast()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [appName, setAppName] = useState('')
  const [supportEmail, setSupportEmail] = useState('')
  const [primaryColor, setPrimaryColor] = useState('#4d71fd')
  const [customDomain, setCustomDomain] = useState('')
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null)
  const [subdomainSlug, setSubdomainSlug] = useState('')
  const [platformDomain, setPlatformDomain] = useState<string | null>(null)
  const [connectHost, setConnectHost] = useState<string | null>(null)
  const [savedDomain, setSavedDomain] = useState<string | null>(null)
  const [domainStatus, setDomainStatus] = useState<DomainStatus | null>(null)
  const [checkingDomain, setCheckingDomain] = useState(false)
  const [uploading, setUploading] = useState<'logo' | 'favicon' | 'poweredByLogo' | null>(null)
  const logoInputRef = useRef<HTMLInputElement>(null)
  const faviconInputRef = useRef<HTMLInputElement>(null)

  // Widget footer "Powered by" — franchise-controlled, shown on every client widget
  const [poweredByEnabled, setPoweredByEnabled] = useState(true)
  const [poweredByText, setPoweredByText] = useState('')
  const [poweredByUrl, setPoweredByUrl] = useState('')
  const [poweredByLogoUrl, setPoweredByLogoUrl] = useState<string | null>(null)
  const poweredByLogoInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    api
      .get<{ success: boolean } & BrandingResponse>('/api/franchise/branding')
      .then(res => {
        const b = res.data.branding || {}
        setAppName(b.appName || '')
        setSupportEmail(b.supportEmail || '')
        setPrimaryColor(HEX_RE.test(b.primaryColor || '') ? b.primaryColor! : '#4d71fd')
        setLogoUrl(b.logoUrl || null)
        setFaviconUrl(b.faviconUrl || null)
        setPoweredByEnabled(b.poweredByEnabled !== false)
        setPoweredByText(b.poweredByText || '')
        setPoweredByUrl(b.poweredByUrl || '')
        setPoweredByLogoUrl(b.poweredByLogoUrl || null)
        setCustomDomain(res.data.customDomain || '')
        setSavedDomain(res.data.customDomain || null)
        setSubdomainSlug(res.data.subdomainSlug || '')
        setPlatformDomain(res.data.platformDomain || null)
        setConnectHost(res.data.connectHost || null)
      })
      .catch(() => {
        toast({ title: 'Could not load branding', description: 'Refresh to try again.', variant: 'destructive' })
      })
      .finally(() => setLoading(false))
  }, [toast])

  const save = async () => {
    setSaving(true)
    try {
      const payload: Record<string, string | boolean | null> = {}
      payload.appName = appName.trim() || null
      payload.supportEmail = supportEmail.trim() || null
      if (HEX_RE.test(primaryColor)) payload.primaryColor = primaryColor
      const domain = customDomain.trim().toLowerCase() || null
      payload.customDomain = domain
      payload.poweredByEnabled = poweredByEnabled
      payload.poweredByText = poweredByText.trim() || null
      payload.poweredByUrl = poweredByUrl.trim() || null
      await api.put('/api/franchise/branding', payload)
      setSavedDomain(domain)
      if (domain !== savedDomain) setDomainStatus(null)
      toast({ title: 'Branding saved', description: 'Your platform now wears your brand.' })
    } catch (err: any) {
      toast({
        title: 'Could not save branding',
        description: err?.response?.data?.message || 'Check the fields and try again.',
        variant: 'destructive'
      })
    } finally {
      setSaving(false)
    }
  }

  const uploadImage = async (kind: 'logo' | 'favicon' | 'poweredByLogo', file: File) => {
    setUploading(kind)
    try {
      // Endpoint paths and multipart field names differ per asset.
      const fieldName = kind === 'logo' ? 'logo' : kind === 'favicon' ? 'favicon' : 'poweredByLogo'
      const path = kind === 'logo' ? 'logo' : kind === 'favicon' ? 'favicon' : 'powered-by-logo'
      const form = new FormData()
      form.append(fieldName, file)
      const res = await api.post(`/api/franchise/branding/${path}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      if (kind === 'logo') setLogoUrl(res.data.logoUrl)
      else if (kind === 'favicon') setFaviconUrl(res.data.faviconUrl)
      else setPoweredByLogoUrl(res.data.poweredByLogoUrl)
      toast({ title: kind === 'logo' ? 'Logo updated' : kind === 'favicon' ? 'Favicon updated' : 'Footer logo updated' })
    } catch (err: any) {
      toast({
        title: 'Upload failed',
        description: err?.response?.data?.message || 'Use a png, jpg, svg, or webp under 512KB.',
        variant: 'destructive'
      })
    } finally {
      setUploading(null)
    }
  }

  const checkDomain = async (silent = false) => {
    if (!savedDomain) return
    if (!silent) setCheckingDomain(true)
    try {
      const res = await api.get<{ success: boolean } & DomainStatus>('/api/franchise/branding/domain-status')
      setDomainStatus(res.data)
      if (!silent && res.data.connected) {
        toast({ title: 'Domain connected', description: `${res.data.domain} is live.` })
      }
    } catch {
      if (!silent) toast({ title: 'Could not check domain', variant: 'destructive' })
    } finally {
      if (!silent) setCheckingDomain(false)
    }
  }

  // While a saved domain is pending, poll quietly so the page flips to
  // Connected on its own once their DNS change propagates
  useEffect(() => {
    if (!savedDomain || domainStatus?.connected) return
    checkDomain(true)
    const interval = setInterval(() => checkDomain(true), 15000)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedDomain, domainStatus?.connected])

  // Registrar "Name/Host" field: the part of their domain before the
  // registrable domain (app.acme.com -> "app"). Apex domains get "@".
  const cnameRecordName = savedDomain && savedDomain.split('.').length >= 3
    ? savedDomain.split('.')[0]
    : '@'

  const [previewBase, previewSuffix] = splitBrandName(appName || 'Your Brand')

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Your Brand"
        subtitle="How your platform looks to every client you bring on — dashboard, login, and emails."
        actions={
          <Button onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save Branding
          </Button>
        }
      />

      {/* Live preview */}
      <Card>
        <CardHeader>
          <CardTitle>Preview</CardTitle>
          <CardDescription>What your clients see in the header and login.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 rounded-lg border border-border bg-background p-5">
            {logoUrl ? (
              <img src={logoUrl} alt="" className="h-9 w-auto" />
            ) : (
              <div
                className="h-9 w-9 rounded-lg"
                style={{ backgroundColor: HEX_RE.test(primaryColor) ? primaryColor : '#4d71fd' }}
              />
            )}
            <span className="font-heading text-xl">
              <span className="font-extrabold text-foreground">{previewBase}</span>
              {previewSuffix && (
                <span className="font-light" style={{ color: HEX_RE.test(primaryColor) ? primaryColor : '#4d71fd' }}>
                  {previewSuffix}
                </span>
              )}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Identity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Palette className="h-4 w-4" /> Identity</CardTitle>
          <CardDescription>Name, color, and the support address your clients will see.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="brand-name">Platform name</Label>
              <Input
                id="brand-name"
                value={appName}
                onChange={e => setAppName(e.target.value)}
                placeholder="e.g. Acme Intake"
                maxLength={120}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="brand-support">Support email</Label>
              <Input
                id="brand-support"
                type="email"
                value={supportEmail}
                onChange={e => setSupportEmail(e.target.value)}
                placeholder="support@youragency.com"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="brand-color">Primary color</Label>
            <div className="flex items-center gap-3">
              <input
                id="brand-color"
                type="color"
                value={HEX_RE.test(primaryColor) ? primaryColor : '#4d71fd'}
                onChange={e => setPrimaryColor(e.target.value)}
                className="h-9 w-12 cursor-pointer rounded border border-border bg-transparent p-1"
              />
              <Input
                value={primaryColor}
                onChange={e => setPrimaryColor(e.target.value)}
                placeholder="#4d71fd"
                className="w-32 font-mono"
                maxLength={7}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Images */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Upload className="h-4 w-4" /> Logo &amp; Favicon</CardTitle>
          <CardDescription>PNG, JPG, SVG, or WebP. Logo up to 512KB, favicon up to 128KB.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6 sm:grid-cols-2">
          <div className="space-y-3">
            <Label>Logo</Label>
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-28 items-center justify-center rounded-lg border border-dashed border-border bg-muted/40 p-2">
                {logoUrl ? <img src={logoUrl} alt="Logo" className="max-h-12 w-auto" /> : <span className="text-xs text-muted-foreground">No logo</span>}
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={uploading === 'logo'}
                onClick={() => logoInputRef.current?.click()}
              >
                {uploading === 'logo' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                Upload logo
              </Button>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden"
                onChange={e => {
                  const f = e.target.files?.[0]
                  if (f) uploadImage('logo', f)
                  e.target.value = ''
                }}
              />
            </div>
          </div>
          <div className="space-y-3">
            <Label>Favicon</Label>
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-dashed border-border bg-muted/40 p-2">
                {faviconUrl ? <img src={faviconUrl} alt="Favicon" className="h-8 w-8" /> : <span className="text-xs text-muted-foreground">None</span>}
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={uploading === 'favicon'}
                onClick={() => faviconInputRef.current?.click()}
              >
                {uploading === 'favicon' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                Upload favicon
              </Button>
              <input
                ref={faviconInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden"
                onChange={e => {
                  const f = e.target.files?.[0]
                  if (f) uploadImage('favicon', f)
                  e.target.value = ''
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Widget footer — "Powered by" */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Widget Footer</CardTitle>
          <CardDescription>
            The “Powered by” line at the bottom of every client widget. This is your attribution —
            your clients never see ours.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <p className="text-sm font-medium">Show “Powered by” footer</p>
              <p className="text-xs text-muted-foreground">Turn off to remove attribution from your clients’ widgets entirely.</p>
            </div>
            <Switch checked={poweredByEnabled} onCheckedChange={setPoweredByEnabled} />
          </div>

          {poweredByEnabled && (
            <>
              {/* Live preview of the footer line */}
              <div className="rounded-lg border border-border bg-muted/40 p-4">
                <p className="mb-2 text-xs font-medium text-muted-foreground">Preview</p>
                <div className="flex items-center justify-center gap-2 rounded-md bg-background py-3">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    Powered by{poweredByLogoUrl ? '' : ` ${poweredByText.trim() || appName.trim() || 'Your Brand'}`}
                  </span>
                  {poweredByLogoUrl && <img src={poweredByLogoUrl} alt="" className="h-[18px] w-auto max-w-[120px] object-contain" />}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="pb-text">Brand name</Label>
                  <Input
                    id="pb-text"
                    value={poweredByText}
                    onChange={e => setPoweredByText(e.target.value)}
                    placeholder={appName || 'Your Agency'}
                    maxLength={60}
                  />
                  <p className="text-xs text-muted-foreground">Name only — the widget shows “Powered by &lt;name&gt;”. Blank uses your platform name.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pb-url">Link (optional)</Label>
                  <Input
                    id="pb-url"
                    value={poweredByUrl}
                    onChange={e => setPoweredByUrl(e.target.value)}
                    placeholder="https://youragency.com"
                  />
                  <p className="text-xs text-muted-foreground">Where the footer links when clicked. Opens in a new tab.</p>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Footer logo (optional)</Label>
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-28 items-center justify-center rounded-lg border border-dashed border-border bg-muted/40 p-2">
                    {poweredByLogoUrl ? <img src={poweredByLogoUrl} alt="Footer logo" className="max-h-8 w-auto" /> : <span className="text-xs text-muted-foreground">No logo</span>}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={uploading === 'poweredByLogo'}
                    onClick={() => poweredByLogoInputRef.current?.click()}
                  >
                    {uploading === 'poweredByLogo' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                    Upload footer logo
                  </Button>
                  <input
                    ref={poweredByLogoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    className="hidden"
                    onChange={e => {
                      const f = e.target.files?.[0]
                      if (f) uploadImage('poweredByLogo', f)
                      e.target.value = ''
                    }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">Small mark shown beside the text. Renders ~18px tall; PNG, JPG, SVG, or WebP up to 256KB.</p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Domain */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Globe className="h-4 w-4" /> Your Domain</CardTitle>
          <CardDescription>
            Run your platform on a domain you own — your clients sign in at your address, not ours.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="brand-domain">Domain</Label>
            <Input
              id="brand-domain"
              value={customDomain}
              onChange={e => setCustomDomain(e.target.value)}
              placeholder="app.youragency.com"
            />
            <p className="text-xs text-muted-foreground">
              Use a subdomain of a domain you own, like <span className="font-mono">app.youragency.com</span> —
              or buy a dedicated domain for your product. Save to get your connection instructions.
            </p>
          </div>

          {savedDomain && domainStatus?.connected && (
            <div className="flex items-center gap-2 rounded-lg border border-green-600/30 bg-green-500/10 px-4 py-3 text-sm">
              <Check className="h-4 w-4 text-green-600" />
              <span className="font-medium text-foreground">{savedDomain}</span>
              <span className="text-muted-foreground">is connected — your clients can sign in there now.</span>
            </div>
          )}

          {savedDomain && !domainStatus?.connected && (
            <div className="space-y-3 rounded-lg border border-border bg-muted/40 p-4">
              <p className="text-sm font-medium text-foreground">
                One step left: add this record where you manage <span className="font-mono">{savedDomain.split('.').slice(-2).join('.')}</span>
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-muted-foreground">
                      <th className="py-1 pr-6">Type</th>
                      <th className="py-1 pr-6">Name / Host</th>
                      <th className="py-1">Value / Points to</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="font-mono">
                      <td className="py-1 pr-6">CNAME</td>
                      <td className="py-1 pr-6">{cnameRecordName}</td>
                      <td className="py-1">{connectHost || 'available once platform DNS goes live'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground">
                Add it in your domain registrar's DNS settings (GoDaddy, Namecheap, Cloudflare…).
                It usually takes a few minutes to take effect — this page checks automatically,
                and the certificate is issued for you. We never need access to your account.
              </p>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={() => checkDomain()} disabled={checkingDomain}>
                  {checkingDomain ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Check connection
                </Button>
                {domainStatus && !domainStatus.cname?.ok && (
                  <span className="text-xs text-muted-foreground">Waiting for your DNS record…</span>
                )}
                {domainStatus?.cname?.ok && !domainStatus.connected && (
                  <span className="text-xs text-muted-foreground">Record found — issuing certificate…</span>
                )}
              </div>
            </div>
          )}

          {subdomainSlug && (
            <p className="text-xs text-muted-foreground">
              Until your domain is connected{platformDomain
                ? <>, your platform stays live at <span className="font-mono">{subdomainSlug}.{platformDomain}</span></>
                : ', your platform stays live at its included address'}.
            </p>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end pb-8">
        <Button onClick={save} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Save Branding
        </Button>
      </div>
    </div>
  )
}
